import React, { useEffect, useMemo, useState } from "react";
import { api, formatTRY } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

export default function CalendarPage() {
  const { t } = useI18n();
  const [schedule, setSchedule] = useState([]);
  const [cursor, setCursor] = useState(() => new Date());

  useEffect(() => {
    api.get("/schedule").then((r) => setSchedule(r.data));
  }, []);

  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startDay = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(y, m + 1, 0).getDate();

  const byDate = useMemo(() => {
    const map = {};
    schedule.forEach((e) => {
      if (!map[e.due_date]) map[e.due_date] = [];
      map[e.due_date].push(e);
    });
    return map;
  }, [schedule]);

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));

  const dayNames = ["Pzt","Sal","Çar","Per","Cum","Cmt","Paz"];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">{t("calendar")}</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-slate-900">
            {t("months")[m]} {y}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => setCursor(new Date(y, m - 1, 1))} className="p-2 border border-slate-200 rounded-md hover:bg-slate-50">
            <CaretLeft size={16} />
          </button>
          <button data-testid="cal-today" onClick={() => setCursor(new Date())} className="px-3 py-1.5 text-xs uppercase tracking-widest font-bold border border-slate-200 rounded-md hover:bg-slate-50">
            Bugün
          </button>
          <button data-testid="cal-next" onClick={() => setCursor(new Date(y, m + 1, 1))} className="p-2 border border-slate-200 rounded-md hover:bg-slate-50">
            <CaretRight size={16} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {dayNames.map((d) => (
            <div key={d} className="p-2 label-eyebrow text-center border-r border-slate-200 last:border-r-0">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="min-h-[110px] border-r border-b border-slate-100 bg-slate-50/50" />;
            const key = ymd(d);
            const events = byDate[key] || [];
            const isToday = ymd(new Date()) === key;
            return (
              <div key={i} className="min-h-[110px] border-r border-b border-slate-100 p-2 last-in-row:border-r-0">
                <div className={`text-xs font-semibold ${isToday ? "text-white bg-slate-900 rounded-full w-6 h-6 inline-flex items-center justify-center" : "text-slate-500"}`}>
                  {d.getDate()}
                </div>
                <div className="mt-1 space-y-1">
                  {events.slice(0, 3).map((e) => (
                    <div
                      key={`${e.tenant_id}-${e.year}-${e.month}`}
                      className={`text-[11px] px-1.5 py-0.5 rounded truncate ${
                        e.status === "paid" ? "bg-emerald-50 text-emerald-700" :
                        e.status === "overdue" ? "bg-red-50 text-red-700" :
                        "bg-slate-100 text-slate-700"
                      }`}
                      title={`${e.tenant_name} — ${formatTRY(e.amount)}`}
                    >
                      {e.tenant_name}
                    </div>
                  ))}
                  {events.length > 3 && <div className="text-[10px] text-slate-400">+{events.length - 3}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
